/**
 * Utility functions for robust data loading with retry mechanisms
 * Prevents hanging and provides consistent error handling across all hooks
 */

interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  timeout?: number;
  exponentialBackoff?: boolean;
}

interface LoaderResult<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
}

/**
 * Generic retry function with exponential backoff
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    timeout = 8000,
    exponentialBackoff = true
  } = options;

  // Prevent retries when tab is not visible to avoid random activity
  if (document.hidden) {
    console.log('⏸️ Skipping retry operation - tab not visible');
    throw new Error('Operation skipped - tab not visible');
  }

  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Check if tab is still visible before each attempt
    if (document.hidden && attempt > 0) {
      console.log('⏸️ Stopping retry attempts - tab not visible');
      throw new Error('Retry stopped - tab not visible');
    }
    
    try {
      // Add timeout to any operation
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Operation timeout after ${timeout}ms`)), timeout)
      );

      const result = await Promise.race([operation(), timeoutPromise]);
      
      // Log successful retry if not first attempt
      if (attempt > 0) {
        console.log(`✅ Operation succeeded on attempt ${attempt + 1}`);
      }
      
      return result;
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry if tab is not visible
      if (document.hidden) {
        console.log('⏸️ Stopping retries - tab not visible');
        break;
      }
      
      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate delay with optional exponential backoff
      const delay = exponentialBackoff 
        ? baseDelay * Math.pow(2, attempt)
        : baseDelay;

      console.warn(`⚠️ Attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error);
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

/**
 * Supabase query wrapper with automatic retry and timeout
 */
export async function supabaseQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: any }>,
  operationName: string,
  options: RetryOptions = {}
): Promise<T | null> {
  console.log(`🔵 ${operationName}: Starting operation`);
  
  try {
    const result = await withRetry(async () => {
      const { data, error } = await queryFn();
      
      if (error) {
        throw new Error(`${operationName} failed: ${error.message}`);
      }
      
      return data;
    }, options);

    console.log(`✅ ${operationName}: Successfully completed`);
    return result;
  } catch (error) {
    console.error(`❌ ${operationName}: Failed after retries:`, error);
    throw error;
  }
}

/**
 * Debounce function to prevent rapid fire requests
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Loading state manager for consistent UX
 */
export class LoadingStateManager {
  private isInitialLoad = true;
  private hasData = false;

  shouldShowLoading(currentDataLength: number): boolean {
    // Only show loading spinner on initial load when we have no data
    if (this.isInitialLoad && currentDataLength === 0) {
      return true;
    }
    return false;
  }

  markDataLoaded(dataLength: number): void {
    this.isInitialLoad = false;
    this.hasData = dataLength > 0;
  }

  reset(): void {
    this.isInitialLoad = true;
    this.hasData = false;
  }
}

/**
 * Memory cleanup utility
 */
export function createCleanupManager() {
  const timeouts: NodeJS.Timeout[] = [];
  const intervals: NodeJS.Timeout[] = [];
  const abortControllers: AbortController[] = [];
  let isCleanedUp = false;

  return {
    addTimeout: (timeout: NodeJS.Timeout) => {
      if (!isCleanedUp) timeouts.push(timeout);
    },
    addInterval: (interval: NodeJS.Timeout) => {
      if (!isCleanedUp) intervals.push(interval);
    },
    addAbortController: (controller: AbortController) => {
      if (!isCleanedUp) abortControllers.push(controller);
    },
    
    cleanup: () => {
      isCleanedUp = true;
      timeouts.forEach(clearTimeout);
      intervals.forEach(clearInterval);
      abortControllers.forEach(controller => controller.abort());
      timeouts.length = 0;
      intervals.length = 0;
      abortControllers.length = 0;
      console.log('🧹 Cleanup completed - all timers and operations stopped');
    }
  };
}